import request from "../../utils/request";
import { Result, AINavigationItem } from "../../models/general";

export type { AINavigationItem };

// AI Chat types
export interface AIChatInput {
  message: string;
  conversationId?: number | null;
  /** When true, the message is processed but not saved to conversation history. */
  ephemeral?: boolean;
}

export interface AIChatResponse {
  message: string;
  conversationId: number;
  /**
   * Deep-links the assistant returns when it answers a "where is X" question via
   * the `find_app_feature` tool. Empty/absent for ordinary replies.
   */
  navigation?: AINavigationItem[];
}

export interface ConversationSummary {
  id: number;
  title: string | null;
  lastMessageAt: string;
  messageCount: number;
}

export interface ConversationDetail {
  id: number;
  title: string | null;
  lastMessageAt: string;
  messages: ChatMessageFromDb[];
}

export interface ChatMessageFromDb {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface AIStatusResponse {
  isConfigured: boolean;
  availableToolsCount: number;
  message: string;
}

/** Capability buckets the widget groups tools under. */
export type AICapabilityGroup = 'leave' | 'attendance' | 'profile' | 'team' | 'navigation';

/**
 * A tool the current user can invoke. The list is already role-filtered
 * server-side. `displayNameKey` / `descriptionKey` / `examplePromptKeys` are
 * i18n keys (render via `t(key)`); `icon` is a lucide icon name.
 */
export interface AIToolInfo {
  name: string;
  description: string;
  category: string;
  isWriteOperation: boolean;
  capabilityGroup: AICapabilityGroup;
  icon: string;
  displayNameKey: string;
  descriptionKey: string;
  examplePromptKeys: string[];
}

/** @deprecated Use {@link AIToolInfo}. Kept as an alias for existing imports. */
export type AIToolDefinition = AIToolInfo;

export interface AIToolsResponse {
  tools: AIToolInfo[];
  count: number;
}

/**
 * Send a message to the AI assistant
 */
export async function sendChatMessage(input: AIChatInput) {
  const response = await request.post<Result<AIChatResponse>>("AI/Chat", input);
  return response.data;
}

/**
 * Get AI service status
 */
export async function getAIStatus() {
  const response = await request.get<Result<AIStatusResponse>>("AI/Status");
  return response.data;
}

/**
 * Get available AI tools
 */
export async function getAITools() {
  const response = await request.get<Result<AIToolsResponse>>("AI/Tools");
  return response.data;
}

/**
 * Get list of user's conversations
 */
export async function getConversations() {
  const response = await request.get<Result<ConversationSummary[]>>("AI/Conversations");
  return response.data;
}

/**
 * Get a specific conversation with messages
 */
export async function getConversation(id: number) {
  const response = await request.get<Result<ConversationDetail>>(`AI/Conversations/${id}`);
  return response.data;
}

/**
 * Delete a conversation
 */
export async function deleteConversation(id: number) {
  const response = await request.delete<Result<boolean>>(`AI/Conversations/${id}`);
  return response.data;
}
